/**
 * Autocomplete de clientes para emissão fiscal e formulários de venda.
 */

import { useState, useEffect, useRef } from 'react'
import { Search, Loader2, User, X } from 'lucide-react'
import { customerService } from '../../../../services/erpApi'
import { formatCpfCnpj } from '../../../../utils/cpfUtils'
import './ClienteSearchAutocomplete.css'

function ClienteSearchAutocomplete({
  selectedCustomer,
  onSelect,
  onClear,
  disabled = false,
  onRequestQuickCreate,
  placeholder = 'Buscar por nome ou documento...'
}) {
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const containerRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (selectedCustomer?.name) {
      setSearchTerm(selectedCustomer.name)
    } else if (!selectedCustomer) {
      setSearchTerm('')
    }
  }, [selectedCustomer])

  useEffect(() => {
    if (!isOpen || selectedCustomer) return

    const search = async () => {
      setLoading(true)
      try {
        const filters = searchTerm.trim()
          ? { search: searchTerm.trim(), limit: 15 }
          : { limit: 15 }
        const res = await customerService.getAll(filters)
        if (res.success) {
          setResults(res.data || [])
        }
      } catch (err) {
        console.warn('Erro ao buscar clientes:', err)
        setResults([])
      } finally {
        setLoading(false)
      }
    }

    const timer = setTimeout(search, searchTerm.trim() ? 280 : 0)
    return () => clearTimeout(timer)
  }, [searchTerm, isOpen, selectedCustomer])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false)
        setHighlightIndex(-1)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (customer) => {
    onSelect?.(customer)
    setSearchTerm(customer.name || '')
    setResults([])
    setIsOpen(false)
    setHighlightIndex(-1)
  }

  const handleClear = () => {
    onClear?.()
    setSearchTerm('')
    setResults([])
    setHighlightIndex(-1)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e) => {
    if (!isOpen || !results.length) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault()
      handleSelect(results[highlightIndex])
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setHighlightIndex(-1)
    }
  }

  return (
    <div className="cliente-search-autocomplete" ref={containerRef}>
      <label htmlFor="cliente-search-input">Cliente (destinatário)</label>

      {selectedCustomer ? (
        <div className="cliente-search-selected">
          <div className="cliente-search-selected-info">
            <User size={16} />
            <div>
              <strong>{selectedCustomer.name}</strong>
              <span>
                {selectedCustomer.cpf_cnpj
                  ? formatCpfCnpj(selectedCustomer.cpf_cnpj)
                  : 'Sem CPF/CNPJ'}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="cliente-search-clear"
            onClick={handleClear}
            disabled={disabled}
            aria-label="Remover cliente selecionado"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div className="cliente-search-input-wrap">
          <Search size={16} className="cliente-search-icon" />
          <input
            id="cliente-search-input"
            ref={inputRef}
            type="text"
            value={searchTerm}
            placeholder={placeholder}
            disabled={disabled}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setIsOpen(true)
              setHighlightIndex(-1)
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          {loading && <Loader2 size={16} className="cliente-search-spinner" />}
        </div>
      )}

      {isOpen && !selectedCustomer && results.length > 0 && (
        <ul className="cliente-search-dropdown" role="listbox">
          {results.map((customer, index) => (
            <li key={customer.id} role="option" aria-selected={highlightIndex === index}>
              <button
                type="button"
                className={`cliente-search-option ${highlightIndex === index ? 'highlighted' : ''}`}
                onMouseEnter={() => setHighlightIndex(index)}
                onClick={() => handleSelect(customer)}
              >
                <strong>{customer.name}</strong>
                <span>
                  {customer.cpf_cnpj ? formatCpfCnpj(customer.cpf_cnpj) : 'Sem documento'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {isOpen && !selectedCustomer && !loading && searchTerm.trim() && results.length === 0 && (
        <p className="cliente-search-empty">Nenhum cliente encontrado</p>
      )}

      {onRequestQuickCreate && (
        <button
          type="button"
          className="cliente-search-quick-link"
          onClick={onRequestQuickCreate}
          disabled={disabled}
        >
          + Cadastrar cliente rápido
        </button>
      )}
    </div>
  )
}

export default ClienteSearchAutocomplete
